import React, { FC, useEffect, useState } from 'react'
import Helmet from 'react-helmet'
import { deleteFreeeLink, getLink, patchFreeeLink } from '../../../../states/apis/link'
import LoadingSquare from '../../../components/common/LoadingSquare'
import { useAlert } from 'react-alert'
import { Link, useLocation, useParams } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Tips from '../../../components/common/Tips'
import { setPath } from '../../../../utils/url'
import { URL } from '../../../constants/url'
import { LangValue } from '../../../../states/types/common'
import { useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'

interface Members {
  id: number
  kana: string | null
  name: string
  profile_image: string | null
  email: string
  code: string
  connect_id: number
}

const Index: FC = () => {
  const alert = useAlert()
  const location = useLocation()
  const { language } = useParams<{ language: LangValue }>()

  const {
    login,
  } = useSelector((state: RootState) => state)

  const [isFreeeConnect, setIsFreeeConnect] = useState<boolean>(false)
  const [freeeConnectUrl, setFreeeConnectUrl] = useState<string>("")

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [isLoadingForFreeeReConnect, setIsLoadingForFreeeReConnect] = useState<boolean>(false)
  const [isLoadingForConnectedMember, setIsLoadingForConnectedMember] = useState<boolean>(false)

  const [connectedMembers, setConnectedMembers] = useState<Members[]>([])

  const [moveDelayedJob, setMoveDelayedJob] = useState<boolean>(false)

  const [isPolling, setIsPolling] = useState<boolean>(false)

  const connectForFreee = () => {
    window.location.href = freeeConnectUrl
  }

  const disconnect = () => {
    deleteFreeeLink()
    setIsFreeeConnect(false)
  }

  const reload = () => {
    setIsLoadingForConnectedMember(true)
    getLink().then((result) => {
      setIsFreeeConnect(result.connect_freee_status)
      setFreeeConnectUrl(result.connect_freee_url)
      setConnectedMembers(result.connect_freee_members)
      setMoveDelayedJob(result.delayed_job)
      if (result.delayed_job) {
        setIsPolling(true)
      }
      setIsLoadingForConnectedMember(false)
    }).catch(() => {
      setIsLoadingForConnectedMember(false)
    })
  }

  const reConnect = () => {
    setIsLoadingForFreeeReConnect(true)
    patchFreeeLink().then(() => {
      alert.success("ユーザー情報の再取得を開始しました。")
      setIsLoadingForFreeeReConnect(false)
      reload()
    }).catch(() => {
      alert.error("ユーザー情報の再取得を開始できませんでした。時間を置いて、もう一度実行してください。")
      setIsLoadingForFreeeReConnect(false)
    })
  }

  useEffect(() => {
    getLink().then((result) => {
      setIsFreeeConnect(result.connect_freee_status)
      setFreeeConnectUrl(result.connect_freee_url)
      setConnectedMembers(result.connect_freee_members)
      setMoveDelayedJob(result.delayed_job)
      if (result.delayed_job) {
        setIsPolling(true)
      }
      setIsLoading(false)
    }).catch(() => {
      setIsLoading(false)
    })
    if (location.state === "freee_success") {
      alert.success("Freee連携が完了しました。")
    } else if (location.state === "freee_error") {
      alert.error("Freee連携が失敗しました。")
    }
  }, [location])

  useEffect(() => {
    if (!isPolling) return
    setIsPolling(false)
    const polling = setTimeout(() => {
      getLink().then((result) => {
        setIsFreeeConnect(result.connect_freee_status)
        setFreeeConnectUrl(result.connect_freee_url)
        setConnectedMembers(result.connect_freee_members)
        setMoveDelayedJob(result.delayed_job)
        if (result.delayed_job) {
          setIsPolling(true)
        }
      }).catch(() => {
        // nothing
      })
    }, 30 * 1000)
    // eslint-disable-next-line consistent-return
    return () => {
      if (!isPolling) {
        clearTimeout(polling)
      }
    }
  }, [isPolling])

  return (
    <section className="setting_page">
      <Helmet>
        <title>{`Qasee - 設定 - ${login.role_name === "管理者" ? "管理者" : "共通"}設定 - 打刻データ連携`}</title>
      </Helmet>
      <section className="breadcrumb">
        設定
        &nbsp;&gt;&nbsp;
        <Link
          to={setPath(URL.SETTING_COMMON.path, language)}
        >
          {`${login.role_name === "管理者" ? "管理者" : "共通"}設定`}
        </Link>
        &nbsp;&gt;&nbsp;
        打刻データ連携
        &nbsp;
      </section>
      <section className="page_header">
        <h1>
          打刻データ連携の設定
        </h1>
      </section>
      <section className="page_description">
        <p>
          各種勤怠システムと連携し、工数管理で仕分け承認された打刻データを自動入力することができます。
        </p>
      </section>
      {
        isLoading ? (
          <>
            <LoadingSquare>
              <>
                連携状態を確認しています…
              </>
            </LoadingSquare>
          </>
        ) : (
          <>
            {
              isFreeeConnect ? (
                <>
                  <section className="page_contents">
                    <div className="content-block">
                      <div className="block-detail api_connect">
                        <div className="detail_block">
                          <p>
                            現在、freee人事労務との連携は有効になっています
                          </p>
                          <button
                            className="button delete"
                            type="button"
                            onClick={disconnect}
                            disabled={isLoadingForFreeeReConnect || moveDelayedJob}
                          >
                            {
                              moveDelayedJob ? (
                                <>
                                  <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                                  ユーザー情報の取得処理実行中のため、操作できません
                                </>
                              ) : (
                                <>
                                  freee人事労務との連携を解除する
                                </>
                              )
                            }
                          </button>
                        </div>
                        <div className="detail_block">
                          <p>
                            ユーザーの追加等、変更があった際にはユーザー情報の再取得を実行してください
                          </p>
                          <p>
                            ※ユーザー情報の取得は順次実行され、完了まで時間がかかることがあります
                          </p>
                          <button
                            className="button info"
                            type="button"
                            onClick={reConnect}
                            disabled={isLoadingForFreeeReConnect || moveDelayedJob}
                          >
                            {
                              moveDelayedJob ? (
                                <>
                                  <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                                  ユーザー情報の取得処理実行中のため、操作できません
                                </>
                              ) : (
                                <>
                                  {
                                    isLoadingForFreeeReConnect ? (
                                      <>
                                        <FontAwesomeIcon icon={['fas', 'spinner']} spin />
                                        再取得処理実行中…
                                      </>
                                    ) : (
                                      <>
                                        ユーザー情報を再取得する
                                      </>
                                    )
                                  }
                                </>
                              )
                            }
                          </button>
                        </div>
                        <div className="connected_member_area">
                          <div className="header_area">
                            <div className="title_area">
                              連携済みユーザー
                            </div>
                            <div className="button_area">
                              <button
                                type="button"
                                className="reload"
                                onClick={reload}
                                disabled={isLoading || moveDelayedJob}
                              >
                                <Tips
                                  description="最新の情報に更新"
                                  className=""
                                  icon={false}
                                >
                                  <>
                                    <FontAwesomeIcon icon={['fas', 'sync-alt']} />
                                  </>
                                </Tips>
                              </button>
                            </div>
                          </div>
                          <div>
                            <p>
                              ※ユーザー情報の取得は順次実行され、完了まで時間がかかることがあります
                            </p>
                          </div>
                          <div className="member_list">
                            {
                              isLoadingForConnectedMember ? (
                                <>
                                  <LoadingSquare>
                                    <>
                                      連携状態を確認しています…
                                    </>
                                  </LoadingSquare>
                                </>
                              ) : (
                                <>
                                  {
                                    connectedMembers.map((member) => (
                                      <>
                                        <div className="member_row">
                                          <div className="row_block name_area">
                                            {
                                              member.profile_image ? (
                                                <img
                                                  alt="profile"
                                                  src={member.profile_image}
                                                  className="profile_icon"
                                                />
                                              ) : (
                                                <img
                                                  alt=""
                                                  src={`/static/images/profile/${(Number(member.id) || 0) % 12}.jpeg`}
                                                  className="profile_icon"
                                                />
                                              )
                                            }
                                            <ruby>
                                              {member.name}
                                              <rt>{member.kana}</rt>
                                            </ruby>
                                            {
                                              member.code && (
                                                <>
                                                  {`(${member.code})`}
                                                </>
                                              )
                                            }
                                          </div>
                                          <div className="row_block">
                                            {member.email}
                                          </div>
                                        </div>
                                      </>
                                    ))
                                  }
                                </>
                              )
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </>
              ) : (
                <>
                  <section className="page_contents">
                    <div className="content-block">
                      <div className="block-detail">
                        <div className="block-header">
                          <div className="title">
                            freee人事労務
                          </div>
                        </div>
                        <div className="block-detail api_connect">
                          <div className="description">
                            <p>freee人事労務と打刻データを連携します。</p>
                            <p className="red">※ freee人事労務のユーザーデータを参照するため、freee人事労務の管理者権限を持つユーザーが連携をおこなってください。</p>
                            <p>
                              詳しい設定方法は
                              <a href="https://qasee.zendesk.com/hc/ja/articles/43458215527833" target="_blank" rel="noreferrer">ヘルプページ</a>
                              をご確認いただくか、カスタマーサクセス窓口までご連絡ください。
                            </p>
                          </div>
                        </div>
                        <div className="block-detail">
                          <button
                            type="button"
                            onClick={connectForFreee}
                            style={{
                              border: "solid 1px #2764F0",
                              borderRadius: "4px",
                              color: "#2764F0",
                            }}
                          >
                            freee人事労務と連携する
                          </button>
                        </div>
                      </div>
                    </div>
                  </section>
                </>
              )
            }
          </>
        )
      }
    </section>
  )
}

export default Index
